import { brands } from "~/config/brands";
import {
  getMarketingBrandFromHostname,
  resolveClientMarketingBrand,
  type MarketingBrandId,
} from "~/lib/marketing-brand";

/** SiteGlue brand palette (official). */
export const SITEGLUE_COLORS = {
  blue: "#4f81bd",
  green: "#9cbb5a",
} as const;

/** Primary brand tagline — used in hero, footer, email signatures, and press. */
export const SITEGLUE_TAGLINE = "Agents Connecting People.";

/** Secondary positioning line — used in feature sections and comparison pages. */
export const SITEGLUE_HITL_LINE =
  "The agent finishes many requests. A person on your Lead Team steps in when needed or when asked.";

/** Product-level descriptor — used in ads, social hooks, and sales. */
export const SITEGLUE_PRODUCT_LINE = "AI that works with your people.";

export type MarketingChrome = {
  brandId: MarketingBrandId;
  productName: string;
  /** Product line shown in pricing / agent labels. */
  agentProductName: string;
  tagline: string;
  logoSrc: string;
  /** Full wordmark + tagline lockup (optional; for hero/about). */
  logoLockupSrc?: string;
  logoAlt: string;
  poweredBy: string;
  companyLegalName: string;
  /** Primary CTA / accent (SiteGlue blue or Inbound GPT primary). */
  primaryColor: string;
  accentColor: string;
  heroEyebrow: string;
  heroHeadline: string;
  heroSubheading: string;
};

/**
 * Resolve marketing chrome for the current host.
 * On app.easygpt.builders, also honors `?brand=siteglue` and session so hire/onboarding
 * mounts SiteGlue consultant UI (not the generic Inbound GPT Trish wizard).
 */
export function getMarketingChrome(hostname?: string): MarketingChrome {
  const host =
    hostname ??
    (typeof window !== "undefined" ? window.location.hostname : "www.easygpt.builders");
  let brandId = getMarketingBrandFromHostname(host);

  // App / localhost hosts are "inbound" by hostname — still treat SiteGlue marketing
  // traffic as SiteGlue when brand query or session is present.
  if (brandId !== "siteglue" && typeof window !== "undefined") {
    const fromClient = resolveClientMarketingBrand(
      new URLSearchParams(window.location.search),
    );
    if (fromClient === "siteglue") {
      brandId = "siteglue";
    }
  }

  if (brandId === "siteglue") {
    const siteglue = brands.siteglue!;
    return {
      brandId,
      productName: "SiteGlue",
      agentProductName: "SiteGlue",
      tagline: SITEGLUE_TAGLINE,
      logoSrc: "/siteglue/logo.png",
      logoLockupSrc: "/siteglue/logo-lockup.png",
      logoAlt: "SiteGlue",
      poweredBy: "Powered by SiteGlue AI",
      companyLegalName: "SiteGlue AI",
      primaryColor: SITEGLUE_COLORS.blue,
      accentColor: SITEGLUE_COLORS.green,
      heroEyebrow: SITEGLUE_TAGLINE,
      heroHeadline: "Is Your Site Agent-Ready?",
      heroSubheading:
        "Scan to see how people (and their agents) find, read and take action on your site.",
    };
  }

  return {
    brandId: "inbound_gpt",
    productName: "Inbound GPT",
    agentProductName: "Inbound GPT",
    tagline: "Connecting humans with AI",
    logoSrc: "/easyGPT-logo.svg",
    logoAlt: "Inbound GPT",
    poweredBy: "Powered by Inbound GPT",
    companyLegalName: "Easy GPT Builders",
    primaryColor: "#3399FF",
    accentColor: "#3399FF",
    heroEyebrow: "Inbound GPT Builder",
    heroHeadline: "Build your own human in the loop agent in minutes",
    heroSubheading: "Connect any conversation to the right person on your team.",
  };
}
